import axios from 'axios';
import { auth } from '../config/firebase';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const eventService = {
    getAllEvents: async (filters = {}) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/events`, { params: filters });
            return response.data;
        } catch (error) {
            if (error.message.includes('Network Error') || error.message.includes('CORS')) {
                throw new Error('Unable to connect to the server. Please check your connection or try again later.');
            }
            throw new Error(error.response?.data?.detail || 'Failed to fetch events');
        }
    },

    getEventById: async (eventId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/events/${eventId}`);
            return response.data;
        } catch (error) {
            if (error.message.includes('Network Error') || error.message.includes('CORS')) {
                throw new Error('Unable to connect to the server. Please check your connection or try again later.');
            }
            throw new Error(error.response?.data?.detail || 'Failed to fetch event');
        }
    },

    getSeatingPlan: async (eventId, subEventId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/events/${eventId}/dates/${subEventId}/seating`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || 'Failed to fetch seating plan');
        }
    },

    getEventDates: async (eventId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/events/${eventId}/dates`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || 'Failed to fetch event dates');
        }
    },

    getFeaturedEvents: async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/events/featured`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || 'Failed to fetch featured events');
        }
    }
};

const seatService = {
    getSeatStatus: async (eventId, subEventId) => {
        const sessionId = localStorage.getItem('sessionId');
        console.log('[API] Getting seat status:', { 
            eventId, 
            subEventId,
            sessionId
        });

        try {
            const response = await axios.get(`${API_BASE_URL}/seats/status`, {
                params: { 
                    session_id: sessionId,
                    release_expired: true,
                    event_id: eventId,
                    sub_event_id: subEventId
                }
            });
            console.log('[API] Seat status response:', {
                data: response.data,
                sessionId
            });
            return response.data;
        } catch (error) {
            console.error('[API] Error getting seat status:', {
                error: error.message,
                response: error.response?.data,
                status: error.response?.status,
                sessionId
            });
            return {};
        }
    },

    reserveSeats: async (eventId, subEventId, seatLabels) => {
        let sessionId = localStorage.getItem('sessionId');
        if (!sessionId) {
            sessionId = Math.random().toString(36).substring(2);
            localStorage.setItem('sessionId', sessionId);
            console.log('[API] Created new session ID:', sessionId);
        }

        console.log('[API] Reserving seats:', { 
            eventId, 
            subEventId, 
            seatLabels,
            sessionId
        });

        try {
            const response = await axios({
                method: 'post',
                url: `${API_BASE_URL}/seats/reserve`,
                params: { session_id: sessionId },
                data: {
                    event_id: eventId,
                    sub_event_id: subEventId,
                    seat_labels: seatLabels.map(label => label.toString())
                }
            });
            
            localStorage.setItem('seatReservationExpires', response.data.expires_at);
            console.log('[API] Reserve seats response:', {
                data: response.data,
                sessionId
            });
            return response.data;
        } catch (error) {
            console.error('[API] Error reserving seats:', {
                error: error.message,
                response: error.response?.data,
                status: error.response?.status,
                sessionId
            });
            if (error.response?.status === 422) {
                throw new Error(error.response.data.detail || 'Seats are not available');
            }
            throw new Error(error.response?.data?.detail || 'Failed to reserve seats');
        }
    },

    releaseSeats: async (eventId, subEventId, seatLabels, sessionId = null) => {
        // Get session ID from parameter or localStorage
        const currentSessionId = sessionId || localStorage.getItem('sessionId');
        
        console.log('[API] Releasing seats:', { 
            eventId, 
            subEventId, 
            seatLabels, 
            sessionId: currentSessionId 
        });

        try {
            if (!seatLabels || seatLabels.length === 0) {
                throw new Error('No seats provided for release');
            }

            if (!currentSessionId) {
                throw new Error('No session ID found for seat release');
            }

            if (!eventId || !subEventId) {
                throw new Error('Event ID and Sub-event ID are required for seat release');
            }

            const formattedSeats = seatLabels.map(seat => 
                typeof seat === 'string' ? seat : seat.label?.toString() || seat.toString()
            );

            const response = await axios({
                method: 'delete',
                url: `${API_BASE_URL}/seats/release`,
                params: { session_id: currentSessionId },
                data: {
                    event_id: eventId,
                    sub_event_id: subEventId,
                    seat_labels: formattedSeats
                }
            });
            
            console.log('[API] Release seats response:', {
                data: response.data,
                sessionId: currentSessionId
            });
            return response.data;
        } catch (error) {
            console.error('[API] Error releasing seats:', {
                error: error.message,
                response: error.response?.data,
                status: error.response?.status,
                sessionId: currentSessionId,
                seatLabels
            });
            throw new Error(error.response?.data?.detail || 'Failed to release seats');
        }
    },

    updateSeatStatus: async (eventId, subEventId, seatLabels, status) => {
        try {
            const response = await axios({
                method: 'put',
                url: `${API_BASE_URL}/seats/status`,
                data: {
                    sub_event_id: subEventId.toString(),
                    seat_labels: seatLabels,
                    status: status
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || 'Failed to update seat status');
        }
    }
};

const generateBookingReference = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `BK-${timestamp}-${random}`.toUpperCase();
};

const bookingService = {
    createBooking: async (bookingData) => {
        try {
            // Add booking reference and ID
            const formattedBookingData = {
                ...bookingData,
                booking_reference: generateBookingReference(),
                _id: new Date().getTime().toString(),  // Generate a unique ID based on timestamp
                created_at: new Date().toISOString()
            };

            // Log the exact data being sent
            console.log('Sending booking data:', JSON.stringify(formattedBookingData, null, 2));

            const response = await axios.post(`${API_BASE_URL}/bookings`, formattedBookingData);
            return response.data;
        } catch (error) {
            // Add detailed error logging
            console.error('Booking error details:', {
                status: error.response?.status,
                data: error.response?.data,
                message: error.message
            });

            if (error.response?.status === 422) {
                throw new Error(error.response.data.detail || 'Invalid booking data format');
            }
            if (error.response?.status === 500) {
                // Check for duplicate key error
                if (error.response.data?.detail?.includes('duplicate key error')) {
                    // Retry once with a new ID
                    try {
                        const retryBookingData = {
                            ...bookingData,
                            booking_reference: generateBookingReference(),
                            _id: new Date().getTime().toString() + Math.random().toString(36).substr(2),
                            created_at: new Date().toISOString()
                        };
                        const retryResponse = await axios.post(`${API_BASE_URL}/bookings`, retryBookingData);
                        return retryResponse.data;
                    } catch (retryError) {
                        throw new Error('Failed to create booking after retry');
                    }
                }
                throw new Error('Server error while creating booking. Please try again.');
            }
            throw new Error(error.response?.data?.detail || 'Failed to create booking');
        }
    },

    getBooking: async (bookingId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/bookings/${bookingId}`);
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || 'Failed to fetch booking details');
        }
    }
};

const userService = {
    getUserDetails: async (userId) => {
        try {
            const token = await auth.currentUser?.getIdToken();
            const response = await axios.get(`${API_BASE_URL}/users/${userId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || 'Failed to fetch user details');
        }
    },

    getUserTickets: async (userId, options = {}) => {
        try {
            const token = await auth.currentUser?.getIdToken();
            const params = options.limit ? { limit: options.limit } : {};
            const response = await axios.get(`${API_BASE_URL}/users/${userId}/tickets`, {
                params,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || 'Failed to fetch user tickets');
        }
    }
};

const ticketService = {
    downloadTicket: async (ticketId) => {
        try {
            const token = await auth.currentUser?.getIdToken();
            const response = await axios.get(`${API_BASE_URL}/tickets/${ticketId}/download`, {
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            return response.data;
        } catch (error) {
            throw new Error(error.response?.data?.detail || 'Failed to download ticket');
        }
    }
};

export {
    eventService,
    seatService,
    bookingService,
    ticketService,
    userService
}; 